import React, { createContext, useContext, useReducer } from 'react';

const CarrinhoContext = createContext();

// Função para carregar estado do localStorage
const loadFromStorage = () => {
  try {
    const saved = localStorage.getItem('sitri_carrinho');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        itens: parsed.itens || [],
        total: parsed.total || 0,
        tipoEntrega: parsed.tipoEntrega || 'local',
        endereco: parsed.endereco || null,
        nomeCliente: parsed.nomeCliente || '',
        mesaCliente: parsed.mesaCliente || '',
        upselVisualizados: parsed.upselVisualizados || [],
        upselsApresentados: parsed.upselsApresentados || [],
        mesa: parsed.mesa || null,
        mesaToken: parsed.mesaToken || null
      };
    }
  } catch (error) {
    // Erro silencioso
  }
  return {};
};

// Estado inicial do carrinho
const initialState = {
  itens: [],
  total: 0,
  comanda: null,
  comandaExistente: null, // Comanda já aberta na mesa
  ultimaComanda: null, // Última comanda criada (delivery/balcão/mesa)
  tipoEntrega: 'local', // 'local' ou 'delivery'
  endereco: null,
  mesa: null,
  mesaToken: null, // Token original da mesa (para polling e re-fetch)
  nomeCliente: '',
  mesaCliente: '',
  brand: null,
  upselVisualizados: [], // IDs dos produtos que já tiveram upsell visualizado
  upselsApresentados: [], // IDs dos upsells já apresentados ao cliente
  ...loadFromStorage() // Carregar dados salvos
};

// Função para salvar no localStorage
const saveToStorage = (state) => {
  try {
    const toSave = {
      itens: state.itens,
      total: state.total,
      tipoEntrega: state.tipoEntrega,
      endereco: state.endereco,
      nomeCliente: state.nomeCliente,
      mesaCliente: state.mesaCliente,
      upselVisualizados: state.upselVisualizados,
      upselsApresentados: state.upselsApresentados,
      mesa: state.mesa,
      mesaToken: state.mesaToken
    };
    localStorage.setItem('sitri_carrinho', JSON.stringify(toSave));
  } catch (error) {
    // Erro silencioso
  }
};

// Reducer para gerenciar o estado do carrinho
function carrinhoReducer(state, action) {
  let newState;
  
  switch (action.type) {
    case 'ADICIONAR_ITEM':
      const novoItem = action.payload;
      const itensAtualizados = [...state.itens, novoItem];
      const novoTotal = itensAtualizados.reduce((total, item) => total + (item.valorTotal * item.quantidade), 0);
      
      newState = {
        ...state,
        itens: itensAtualizados,
        total: novoTotal
      };
      saveToStorage(newState);
      return newState;

    case 'REMOVER_ITEM':
      const itensRestantes = state.itens.filter((_, index) => index !== action.payload);
      const totalRestante = itensRestantes.reduce((total, item) => total + (item.valorTotal * item.quantidade), 0);
      
      newState = {
        ...state,
        itens: itensRestantes,
        total: totalRestante
      };
      saveToStorage(newState);
      return newState;

    case 'ATUALIZAR_QUANTIDADE':
      const { index, quantidade } = action.payload;
      const itensComQuantidadeAtualizada = state.itens.map((item, i) => 
        i === index ? { ...item, quantidade } : item
      );
      const totalComQuantidadeAtualizada = itensComQuantidadeAtualizada.reduce((total, item) => total + (item.valorTotal * item.quantidade), 0);
      
      newState = {
        ...state,
        itens: itensComQuantidadeAtualizada,
        total: totalComQuantidadeAtualizada
      };
      saveToStorage(newState);
      return newState;

    case 'LIMPAR_CARRINHO':
      newState = {
        ...state,
        itens: [],
        total: 0,
        upselVisualizados: [],
        upselsApresentados: []
      };
      saveToStorage(newState);
      return newState;

    // Limpa itens mas PRESERVA mesa e mesaToken (para pedidos em mesa - cenário QR code)
    case 'LIMPAR_ITENS_MESA':
      newState = {
        ...state,
        itens: [],
        total: 0,
        upselVisualizados: [],
        upselsApresentados: []
      };
      saveToStorage(newState);
      return newState;

    // Reset total - zera TUDO incluindo mesa (para tablet quando PDV fecha)
    case 'RESET_MESA_COMPLETO':
      newState = {
        ...state,
        itens: [],
        total: 0,
        comanda: null,
        comandaExistente: null,
        ultimaComanda: null,
        mesa: null,
        mesaToken: null,
        nomeCliente: '',
        mesaCliente: '',
        upselVisualizados: [],
        upselsApresentados: []
      };
      saveToStorage(newState);
      localStorage.removeItem('sitri_ultima_comanda');
      return newState;

    case 'SET_TIPO_ENTREGA':
      newState = {
        ...state,
        tipoEntrega: action.payload,
        // Limpar mesa quando mudar para delivery ou balcão
        mesa: (action.payload === 'delivery' || action.payload === 'balcao') ? null : state.mesa
      };
      saveToStorage(newState);
      return newState;

    case 'SET_ENDERECO':
      newState = {
        ...state,
        endereco: action.payload
      };
      saveToStorage(newState);
      return newState;

    case 'SET_MESA':
      newState = {
        ...state,
        mesa: action.payload
      };
      saveToStorage(newState);
      return newState;

    case 'SET_MESA_TOKEN':
      newState = {
        ...state,
        mesaToken: action.payload
      };
      saveToStorage(newState);
      return newState;

    case 'SET_NOME_CLIENTE':
      newState = {
        ...state,
        nomeCliente: action.payload
      };
      saveToStorage(newState);
      return newState;

    case 'SET_MESA_CLIENTE':
      newState = {
        ...state,
        mesaCliente: action.payload
      };
      saveToStorage(newState);
      return newState;

    case 'SET_COMANDA':
      return {
        ...state,
        comanda: action.payload
      };

    case 'SET_BRAND':
      return {
        ...state,
        brand: action.payload
      };

    case 'SET_COMANDA_EXISTENTE':
      // Evitar re-renderizações desnecessárias se a comanda for a mesma
      if (state.comandaExistente?.id === action.payload?.id) {
        return state;
      }
      return {
        ...state,
        comandaExistente: action.payload
      };

    case 'SET_ULTIMA_COMANDA':
      // Evitar re-renderizações desnecessárias se a comanda for a mesma
      if (state.ultimaComanda?.id === action.payload?.id) {
        return state;
      }
      return {
        ...state,
        ultimaComanda: action.payload
      };

    case 'ADD_UPSEL_VISUALIZADO':
      newState = {
        ...state,
        upselVisualizados: [...state.upselVisualizados, action.payload]
      };
      saveToStorage(newState);
      return newState;

    case 'LIMPAR_UPSEL_VISUALIZADOS':
      newState = {
        ...state,
        upselVisualizados: []
      };
      saveToStorage(newState);
      return newState;

    case 'ADD_UPSEL_APRESENTADO':
      newState = {
        ...state,
        upselsApresentados: [...state.upselsApresentados, action.payload]
      };
      saveToStorage(newState);
      return newState;

    default:
      return state;
  }
}

// Provider do contexto
export function CarrinhoProvider({ children, brand }) {
  const [state, dispatch] = useReducer(carrinhoReducer, { ...initialState, brand });

  const adicionarItem = (produto, variacoes = [], adicionais = [], opcionais = [], comentario = '', compositeData = null) => {
    // Se é produto composto ou combo, usar fluxo simplificado com dados do wizard
    if (compositeData && (compositeData.tipo_produto === 'composto' || compositeData.tipo_produto === 'combo')) {
      const item = {
        produto,
        variacoes: [],
        adicionais: [],
        opcionais: [],
        valorTotal: parseFloat(compositeData.valor || 0),
        quantidade: 1,
        complemento: comentario || null,
        tipo_produto: compositeData.tipo_produto,
        ...(compositeData.tipo_produto === 'composto' && { etapas: compositeData.etapas }),
        ...(compositeData.tipo_produto === 'combo' && { grupos: compositeData.grupos }),
      };

      dispatch({ type: 'ADICIONAR_ITEM', payload: item });
      return;
    }

    // Se a variação selecionada é um SKU (sub-produto), usar o id do SKU como produto_id
    // para que a ficha técnica correta seja usada na baixa de estoque
    let produtoParaCarrinho = produto;
    if (variacoes.length > 0 && variacoes[0].is_sku) {
      produtoParaCarrinho = {
        ...produto,
        id: variacoes[0].id,
        nome: `${produto.nome} - ${variacoes[0].nome}`,
      };
      variacoes = [];
    }

    // Separar adicionais e opcionais baseado no b_adicional
    const adicionaisReais = [];
    const opcionaisReais = [...opcionais];
    
    adicionais.forEach(item => {
      if (item.b_adicional === 0) {
        opcionaisReais.push(item);
      } else {
        adicionaisReais.push(item);
      }
    });
    
    // Calcular valor total do item
    let valorTotal;
    
    // Se há variação, usar apenas o valor da variação, senão usar o valor base do produto
    if (variacoes.length > 0) {
      valorTotal = parseFloat(variacoes[0].valor || 0);
    } else {
      // Usar valor com desconto se disponível, senão usar valor original
      const valorComDesconto = produtoParaCarrinho.em_promocao && produtoParaCarrinho.promocao_info?.valor_promocional 
        ? produtoParaCarrinho.promocao_info.valor_promocional 
        : produtoParaCarrinho.valor;
      valorTotal = parseFloat(valorComDesconto);
    }
    
    // Adicionar valor dos adicionais para exibição no carrinho
    adicionaisReais.forEach(adicional => {
      valorTotal += parseFloat(adicional.valor || 0);
    });
    
    // Log dos opcionais (sem valor)
    opcionaisReais.forEach(opcional => {
    });

    const item = {
      produto: produtoParaCarrinho,
      variacoes,
      adicionais: adicionaisReais,
      opcionais: opcionaisReais,
      valorTotal,
      quantidade: 1,
      complemento: comentario || null
    };

    dispatch({ type: 'ADICIONAR_ITEM', payload: item });
  };

  const removerItem = (index) => {
    dispatch({ type: 'REMOVER_ITEM', payload: index });
  };

  const atualizarQuantidade = (index, quantidade) => {
    dispatch({ type: 'ATUALIZAR_QUANTIDADE', payload: { index, quantidade } });
  };

  const limparCarrinho = () => {
    dispatch({ type: 'LIMPAR_CARRINHO' });
  };

  const setTipoEntrega = (tipo) => {
    dispatch({ type: 'SET_TIPO_ENTREGA', payload: tipo });
  };

  const setEndereco = (endereco) => {
    dispatch({ type: 'SET_ENDERECO', payload: endereco });
  };

  const setMesa = (mesa) => {
    dispatch({ type: 'SET_MESA', payload: mesa });
  };

  const setMesaToken = (token) => {
    dispatch({ type: 'SET_MESA_TOKEN', payload: token });
  };

  // Limpa itens do carrinho mas preserva mesa (para pedidos em mesa - QR code)
  const limparItensMesa = () => {
    dispatch({ type: 'LIMPAR_ITENS_MESA' });
  };

  // Reset total - zera tudo incluindo mesa (para tablet quando PDV fecha)
  const resetMesaCompleto = () => {
    dispatch({ type: 'RESET_MESA_COMPLETO' });
  };

  const setNomeCliente = (nome) => {
    dispatch({ type: 'SET_NOME_CLIENTE', payload: nome });
  };

  const setMesaCliente = (mesa) => {
    dispatch({ type: 'SET_MESA_CLIENTE', payload: mesa });
  };

  const setComanda = (comanda) => {
    dispatch({ type: 'SET_COMANDA', payload: comanda });
  };

  const setBrand = (brand) => {
    dispatch({ type: 'SET_BRAND', payload: brand });
  };

  const setComandaExistente = (comanda) => {
    dispatch({ type: 'SET_COMANDA_EXISTENTE', payload: comanda });
  };

  const setUltimaComanda = (comanda) => {
    dispatch({ type: 'SET_ULTIMA_COMANDA', payload: comanda });
  };

  const addUpselVisualizado = (produtoId) => {
    dispatch({ type: 'ADD_UPSEL_VISUALIZADO', payload: produtoId });
  };

  const limparUpselVisualizados = () => {
    dispatch({ type: 'LIMPAR_UPSEL_VISUALIZADOS' });
  };

  const addUpselApresentado = (produtoId) => {
    dispatch({ type: 'ADD_UPSEL_APRESENTADO', payload: produtoId });
  };

  const limparCarrinhoStorage = () => {
    try {
      localStorage.removeItem('sitri_carrinho');
    } catch (error) {
      // Erro silencioso
    }
  };

  const value = {
    ...state,
    adicionarItem,
    removerItem,
    atualizarQuantidade,
    limparCarrinho,
    limparItensMesa,
    resetMesaCompleto,
    setTipoEntrega,
    setEndereco,
    setMesa,
    setMesaToken,
    setNomeCliente,
    setMesaCliente,
    setComanda,
    setBrand,
    setComandaExistente,
    setUltimaComanda,
    addUpselVisualizado,
    limparUpselVisualizados,
    addUpselApresentado,
    limparCarrinhoStorage
  };

  return (
    <CarrinhoContext.Provider value={value}>
      {children}
    </CarrinhoContext.Provider>
  );
}

// Hook para usar o contexto
export function useCarrinho() {
  const context = useContext(CarrinhoContext);
  if (!context) {
    throw new Error('useCarrinho deve ser usado dentro de um CarrinhoProvider');
  }
  return context;
}